// AI Labs Tool Configuration
// Icons: Lucide icon names (rendered in components)

// Text Tools (column 1)
export const TEXT_TOOLS = {
  title: {
    id: 'title',
    name: 'Headline',
    icon: 'Heading',
    description: '7 headline styles',
    placeholder: 'Paste the article you want to create a headline for...',
    greeting: "I'll generate 7 headline styles for you",
    color: 'blue',
    endpoint: 't1',
    engine: 'H8',
    examples: [
      'Make it a click-inducing headline',
      'Optimize for SEO',
      'Include a social media version too',
    ],
    info: [
      'Journalism-first',
      'Balanced hook',
      'Click-bait',
      'SEO-optimized',
      'Social media',
      'Keyword-optimized',
      'Viral',
    ],
    subTypes: [
      { id: 't5', name: '5 Types', icon: 'Type', engine: 't5', description: '5 differentiated headline types including click-bait', detail: 'Journalism-first, balanced hook, and more' },
      { id: 'c7', name: 'Creative 7', icon: 'Flame', engine: 'c7', description: 'Language innovation, collision, emotional amplification engine', detail: '7 engines to spark fresh headline ideas' },
    ],
  },
  proofread: {
    id: 'proofread',
    name: 'Proofread',
    icon: 'PenLine',
    description: 'Spelling, grammar & expression',
    placeholder: 'Paste the article you want to proofread...',
    greeting: "I'll polish your article",
    color: 'sedaily',
    endpoint: 'p1',
    engine: 'Basic',
    examples: [
      'Check spelling, grammar, and expressions',
      'Check spacing only',
      'Proofread in SE Daily style',
    ],
    subTypes: [
      { id: 'business', name: 'Business', icon: 'Briefcase', engine: 'Business', description: 'Perfect spell-check for numbers, units & financial terms', detail: 'Subject-predicate agreement, redundancy removal' },
      { id: 'general', name: 'General News', icon: 'Newspaper', engine: 'General News', description: 'Quote accuracy and indirect speech grammar check', detail: 'Passive voice improvement, paragraph flow review' },
    ],
  },
  press: {
    id: 'press',
    name: 'Press Release',
    icon: 'Newspaper',
    description: 'Convert press releases to articles',
    placeholder: 'Paste the press release...',
    greeting: "I'll convert the press release into an article",
    color: 'emerald',
    endpoint: 'w1',
    engine: '22',
    examples: [
      'Focus on economic impact',
      'Restructure from the reader perspective',
      'Summarize around key figures',
    ],
    subTypes: [
      { id: 'gov', name: 'Government/Public', icon: 'Landmark', engine: 'Government:Public', description: 'Draft articles from government & public institution releases', detail: 'Economic policy impact analysis, real estate & finance regulation summary' },
      { id: 'corp', name: 'Corporate', icon: 'Building2', engine: 'Corporate', description: 'Draft articles from corporate press releases', detail: 'New product/service highlights, quarterly earnings analysis' },
    ],
  },
  foreign: {
    id: 'foreign',
    name: 'Foreign News',
    icon: 'Globe',
    description: 'Translate & summarize foreign news',
    placeholder: 'Paste the foreign news article to translate...',
    greeting: "I'll translate the foreign news for you",
    color: 'rose',
    endpoint: 'f1',
    engine: '11',
    examples: [
      'Translate naturally into English',
      'Summarize the key points only',
      'Reframe for a local audience',
    ],
    subTypes: [
      { id: 'english', name: 'English', icon: 'Globe', engine: 'English', description: 'Draft articles from English-language foreign news', detail: 'Real-time translation of CNBC, Reuters, Bloomberg, etc.' },
      { id: 'japanese', name: 'Japanese', icon: 'Globe2', engine: 'Japanese', description: 'Draft articles from Japanese-language foreign news', detail: 'Nikkei, Kyodo News — localized terminology & context' },
    ],
  },
  column: {
    id: 'column',
    name: 'Revise',
    icon: 'FileEdit',
    description: 'Improve column/article structure',
    placeholder: 'Paste the article you want to revise...',
    greeting: "I'll improve your article structure",
    color: 'amber',
    endpoint: 'r1',
    engine: '22',
    examples: [
      'Make the flow more natural',
      'Restructure to reduce reader drop-off',
      'Rewrite the intro with more impact',
    ],
    subTypes: [
      { id: 'structure', name: 'Structure Analysis', icon: 'FileText', engine: 'Structure Analysis', description: 'Predict reader drop-off points and suggest restructuring', detail: 'Hero\'s journey / mystery / flashback structure options' },
      { id: 'quick', name: 'Quick Edit', icon: 'Zap', engine: 'Quick Edit', description: 'First-2-second impact to reduce reader drop-off', detail: '3 improvement directions — pick one instantly' },
    ],
  },
  buddy: {
    id: 'buddy',
    name: 'Buddy',
    icon: 'Bot',
    description: 'Article writing partner',
    placeholder: 'Enter your idea or reporting notes...',
    greeting: 'AI Buddy is here to help',
    color: 'pink',
    endpoint: 'b1',
    engine: '11',
    examples: [
      "What should I write today? Give me ideas",
      'Draft a story brief from this press release',
      'Write an article draft from my notes',
    ],
    subTypes: [
      { id: 'ilbo', name: 'Brief Buddy', icon: 'Lightbulb', engine: 'Brief Buddy', description: 'Turn even a simple idea into a thorough story brief', detail: 'Enter just one word — get 3 article directions' },
      { id: 'article', name: 'Article Buddy', icon: 'FileSignature', engine: 'Article Buddy', description: 'Input a brief and facts to get a full article draft', detail: '[Brackets] clearly mark items that need verification' },
    ],
  },
  prism: {
    id: 'prism',
    name: 'PRISM',
    icon: 'Sparkles',
    description: 'Curate articles by reader type',
    placeholder: 'Enter a list of articles or topics...',
    greeting: 'AI curates key articles tailored to each reader type',
    color: 'sky',
    endpoint: 'prism',
    engine: 'prism',
    comingSoon: true,
    examples: [
      'Pick articles from today that Gen Z would like',
      'Select news relevant to startup founders',
      'Pull out the most important articles for stock investors',
    ],
    audienceTypes: [
      { id: 'student', name: 'Students & Job Seekers', icon: 'GraduationCap', description: 'Jobs, self-development, trends' },
      { id: 'newbie', name: 'Early-career Professionals', icon: 'Briefcase', description: 'Work life, personal finance basics' },
      { id: 'startup', name: 'Startups', icon: 'Rocket', description: 'Investment, entrepreneurship, tech' },
      { id: 'global', name: 'Global Investors', icon: 'Globe2', description: 'Overseas markets, FX, global economy' },
      { id: 'ceo', name: 'Corporate CEOs', icon: 'Crown', description: 'Strategy, M&A, policy' },
      { id: 'finance', name: 'Financial Products', icon: 'Wallet', description: 'Deposits, insurance, funds' },
      { id: 'realestate', name: 'Real Estate', icon: 'Home', description: 'Sales, leasing, policy, development' },
      { id: 'stock', name: 'Stock Investors', icon: 'TrendingUp', description: 'Stock analysis, market trends, disclosures' },
    ],
    guide: {
      title: 'PRISM Usage Guide',
      tips: [
        { label: 'Select type', example: 'Choose the reader type you are targeting' },
        { label: 'Input articles', example: 'Paste an article list or RSS feed' },
        { label: 'Curated results', example: 'AI selects the best articles for each type' },
      ],
      notice: 'You can select multiple reader types at once',
    },
  },
};

// Visual Tools (column 2)
export const VISUAL_TOOLS = {
  table: {
    id: 'table',
    name: 'Table',
    icon: 'Table',
    description: 'Organize data into a table',
    placeholder: 'Enter the data or content you want to turn into a table...',
    greeting: "I'll organize your data into a table",
    color: 'cyan',
    endpoint: 'visual',
    engine: 'table',
    category: 'visual',
    comingSoon: false,
    examples: [
      'Create a comparison table of Samsung and SK Hynix 2024 earnings',
      'Organize quarterly revenue trends for 2024 into a table',
      'Top 10 IT companies by market cap comparison table',
    ],
  },
  chart_convert: {
    id: 'chart_convert',
    name: 'Chart Convert',
    icon: 'Palette',
    description: 'Change colors & erase regions',
    placeholder: 'Upload a chart image...',
    greeting: "I'll convert your chart image",
    color: 'violet',
    endpoint: 'visual',
    engine: 'chart_convert',
    category: 'visual',
    comingSoon: false,
    isImageTool: true,
    examples: [],
  },
  chart: {
    id: 'chart',
    name: 'Chart',
    icon: 'BarChart3',
    description: 'Bar / line / pie charts',
    placeholder: 'Enter the data you want to visualize as a chart...',
    greeting: "I'll visualize your data as a chart",
    color: 'violet',
    endpoint: 'visual',
    engine: 'chart',
    category: 'visual',
    comingSoon: true,
    examples: [
      'Line graph of semiconductor market size 2020–2024',
      'Global smartphone market share pie chart',
      'Quarterly operating profit bar chart',
    ],
    subTypes: [
      { id: 'bar', name: 'Bar Chart', icon: 'BarChart', description: 'Compare items' },
      { id: 'line', name: 'Line Chart', icon: 'LineChart', description: 'Trend analysis' },
      { id: 'pie', name: 'Pie Chart', icon: 'PieChart', description: 'Show proportions' },
    ],
  },
  chart_slice: {
    id: 'chart_slice',
    name: 'Chart Slice',
    icon: 'Crop',
    description: 'Extract a section from a chart image',
    placeholder: 'Upload a chart image...',
    greeting: "I'll extract the section you want from the chart",
    color: 'violet',
    endpoint: 'visual',
    engine: 'chart_slice',
    category: 'visual',
    comingSoon: true,
    isImageTool: true,
    examples: [],
  },
  table_extract: {
    id: 'table_extract',
    name: 'Table Extract',
    icon: 'ScanLine',
    description: 'Extract a table from a screenshot',
    placeholder: 'Upload a screenshot containing a table...',
    greeting: "I'll extract the table from your screenshot",
    color: 'cyan',
    endpoint: 'visual',
    engine: 'table_extract',
    category: 'visual',
    comingSoon: false,
    hidden: true,
    isImageTool: true,
    examples: [],
  },
  infographic: {
    id: 'infographic',
    name: 'Infographic',
    icon: 'LayoutDashboard',
    badge: '🍌',
    description: 'Information visualization graphic',
    placeholder: 'Enter the content you want to turn into an infographic...',
    greeting: "I'll generate an infographic with Nanobanana",
    color: 'orange',
    endpoint: 'visual',
    engine: 'nanobanana',
    category: 'visual',
    linkedModelId: 'nanobanana',
    comingSoon: true,
    examples: [
      'Create an infographic on EV battery types and features',
      'Visualize key climate change statistics at a glance',
      'Visualize the startup fundraising process',
    ],
    subTypes: [
      { id: 'stats', name: 'Stats', icon: 'BarChart2', description: 'Highlight numbers' },
      { id: 'process', name: 'Process', icon: 'Workflow', description: 'Steps/flow' },
      { id: 'compare', name: 'Comparison', icon: 'Columns', description: 'A vs B' },
    ],
    guide: {
      title: 'Generation Guide',
      tips: [
        { label: 'Topic + format', example: 'Semiconductor market share comparison infographic' },
        { label: 'Include data', example: 'Samsung 40%, SK 25%, Micron 15% market share visualization' },
        { label: 'Specify style', example: 'Explain 3 ESG management elements in a minimal style' },
      ],
      notice: 'Including only key figures for complex data yields better results',
    },
  },
  image: {
    id: 'image',
    name: 'Image',
    icon: 'Image',
    badge: '🍌',
    description: 'AI image generation',
    placeholder: 'Describe the image you want to generate...',
    greeting: "I'll generate an image with Nanobanana",
    color: 'fuchsia',
    endpoint: 'visual',
    engine: 'nanobanana',
    category: 'visual',
    linkedModelId: 'nanobanana',
    comingSoon: true,
    examples: [
      'Futuristic city with autonomous vehicles',
      'Eco-friendly energy concept illustration',
      'Abstract fintech image',
    ],
    subTypes: [
      { id: 'realistic', name: 'Realistic', icon: 'Camera', description: 'Photorealistic' },
      { id: 'illustration', name: 'Illustration', icon: 'Palette', description: 'Graphic style' },
      { id: 'abstract', name: 'Abstract', icon: 'Shapes', description: 'Concept art' },
    ],
    notice: 'AI-generated images must be labeled as such',
    guide: {
      title: 'Generation Guide',
      tips: [
        { label: 'Topic + style', example: 'Futuristic cityscape, illustration style' },
        { label: 'Add mood', example: 'Warm-toned eco-friendly smart farm image' },
        { label: 'Be specific', example: 'Aerial view of Gangnam skyscrapers at sunset' },
      ],
      notice: 'Real people, brand logos, and copyrighted characters cannot be generated',
    },
  },
  // timeline: {
  //   id: 'timeline',
  //   name: 'Timeline',
  //   icon: 'Clock',
  //   description: 'Visualize events in chronological order',
  //   placeholder: 'Enter the events you want to put on a timeline...',
  //   greeting: "I'll organize them in chronological order",
  //   color: 'teal',
  //   endpoint: 'visual',
  //   engine: 'timeline',
  //   category: 'visual',
  //   comingSoon: true,
  //   examples: [
  //     'Samsung Electronics semiconductor history timeline',
  //     'Key economic events of 2024',
  //     'Startup growth chronicle',
  //   ],
  // },
  // map: {
  //   id: 'map',
  //   name: 'Map',
  //   icon: 'MapPin',
  //   description: 'Visualize regions/locations',
  //   placeholder: 'Enter the information you want to display on a map...',
  //   greeting: "I'll display the information on a map",
  //   color: 'lime',
  //   endpoint: 'visual',
  //   engine: 'map',
  //   category: 'visual',
  //   comingSoon: true,
  //   examples: [
  //     'Map of semiconductor cluster locations nationwide',
  //     'Mark major redevelopment areas in Seoul',
  //     'Global logistics hub distribution map',
  //   ],
  // },
};

// Audio Tools (column 3) - ElevenLabs integration
export const AUDIO_TOOLS = {
  tts: {
    id: 'tts',
    name: 'TTS',
    icon: 'Volume2',
    description: 'Convert text to speech',
    placeholder: 'Enter the text you want to convert to speech...',
    greeting: "🎙️ I'll convert your text into natural-sounding speech",
    color: 'indigo',
    endpoint: 'audio',
    engine: 'elevenlabs',
    category: 'audio',
    linkedModelId: 'elevenlabs',
    comingSoon: true,
    examples: [
      'Read this article in a news anchor style',
      'Convert this press release to audio',
      'Narrate this column in a calm voice',
    ],
    subTypes: [
      { id: 'news', name: 'News Anchor', icon: 'Radio', description: 'Professional news tone' },
      { id: 'casual', name: 'Casual', icon: 'Smile', description: 'Friendly conversational tone' },
      { id: 'formal', name: 'Formal', icon: 'Briefcase', description: 'Official tone' },
    ],
    guide: {
      title: 'TTS Usage Guide',
      tips: [
        { label: 'Full article', example: '"Read this entire article"' },
        { label: 'Specify style', example: '"Read it professionally like a news anchor"' },
        { label: 'Adjust speed', example: '"Read slowly and clearly"' },
      ],
      notice: 'Up to 5,000 characters can be converted',
    },
  },
  podcast: {
    id: 'podcast',
    name: 'Podcast',
    icon: 'Podcast',
    description: '2-person dialogue podcast',
    placeholder: 'Enter the article or topic you want to turn into a podcast...',
    greeting: "🎧 I'll convert your article into a 2-person dialogue podcast",
    color: 'purple',
    endpoint: 'audio',
    engine: 'elevenlabs-podcast',
    category: 'audio',
    linkedModelId: 'elevenlabs',
    comingSoon: true,
    examples: [
      'Turn this economics article into a podcast',
      'Discuss semiconductor market trends in dialogue format',
      "Today's market briefing as a podcast",
    ],
    subTypes: [
      { id: 'news', name: 'News Talk', icon: 'Newspaper', description: 'Anchor + reporter dialogue' },
      { id: 'expert', name: 'Expert Interview', icon: 'Users', description: 'Host + expert' },
      { id: 'casual', name: 'Casual Talk', icon: 'MessageCircle', description: 'Friendly conversation' },
    ],
    guide: {
      title: 'Podcast Generation Guide',
      tips: [
        { label: 'Paste article', example: '"Turn this article into a podcast"' },
        { label: 'Choose style', example: '"Make it an expert interview style"' },
        { label: 'Set focus', example: '"Focus the conversation on investment points"' },
      ],
      notice: 'Generates a natural 2-person dialogue in NotebookLM style',
    },
  },
  // webtoon: {
  //   id: 'webtoon',
  //   name: 'Webtoon',
  //   icon: 'Clapperboard',
  //   description: 'Convert articles to webtoon scripts',
  //   placeholder: 'Enter the article or content you want to turn into a webtoon...',
  //   greeting: "I'll convert your article into a webtoon script",
  //   color: 'rose',
  //   endpoint: 'content',
  //   engine: 'webtoon',
  //   category: 'audio',
  //   comingSoon: true,
  //   examples: [
  //     'Turn this economics article into a 4-panel webtoon',
  //     'Explain semiconductor industry issues in an easy webtoon script',
  //     'Turn stock investment tips into a fun webtoon',
  //   ],
  //   subTypes: [
  //     { id: '4cut', name: '4-Panel', icon: 'LayoutGrid', description: 'Simple 4-panel format' },
  //     { id: 'story', name: 'Story', icon: 'BookOpen', description: 'Long-form story' },
  //     { id: 'explainer', name: 'Explainer', icon: 'HelpCircle', description: 'Concept explanation' },
  //   ],
  //   guide: {
  //     title: 'Webtoon Script Guide',
  //     tips: [
  //       { label: 'Input article', example: '"Turn this article into a 4-panel webtoon"' },
  //       { label: 'Specify style', example: '"Make it humorous"' },
  //       { label: 'Target audience', example: '"Make it easy for Gen Z to understand"' },
  //     ],
  //     notice: 'Generates a script with character settings, dialogue, and scene descriptions',
  //   },
  // },
  // youtube: {
  //   id: 'youtube',
  //   name: 'YouTube',
  //   icon: 'Youtube',
  //   description: 'Generate title, description & hashtags',
  //   placeholder: 'Enter the article or topic you want to turn into YouTube content...',
  //   greeting: "I'll generate an SEO-optimized title, description, and hashtags for YouTube",
  //   color: 'red',
  //   endpoint: 'content',
  //   engine: 'youtube',
  //   category: 'audio',
  //   comingSoon: true,
  //   examples: [
  //     'Create a YouTube video description from this economics article',
  //     'Recommend hashtags for semiconductor news',
  //     '5 click-bait titles for investment content',
  //   ],
  //   subTypes: [
  //     { id: 'title', name: 'Title', icon: 'Type', description: 'Click-inducing title' },
  //     { id: 'description', name: 'Description', icon: 'AlignLeft', description: 'Video description' },
  //     { id: 'hashtag', name: 'Hashtags', icon: 'Hash', description: 'SEO hashtags' },
  //     { id: 'all', name: 'All', icon: 'Package', description: 'Title + description + tags' },
  //   ],
  //   guide: {
  //     title: 'YouTube Content Guide',
  //     tips: [
  //       { label: 'Input article', example: '"Create a YouTube description from this article"' },
  //       { label: 'Specify style', example: '"Make the title highly engaging"' },
  //       { label: 'Target audience', example: '"This is for beginner investors"' },
  //     ],
  //     notice: 'Includes SEO elements optimized for the YouTube algorithm',
  //   },
  // },
};

// All tools combined
export const TOOLS = { ...TEXT_TOOLS, ...VISUAL_TOOLS, ...AUDIO_TOOLS };

// Text tool list (column 1)
export const TEXT_TOOL_LIST = Object.values(TEXT_TOOLS);

// Visual tool list (column 2)
export const VISUAL_TOOL_LIST = Object.values(VISUAL_TOOLS).filter(t => !t.hidden);

// Audio tool list (column 3)
export const AUDIO_TOOL_LIST = Object.values(AUDIO_TOOLS);

// All tools list
export const TOOL_LIST = Object.values(TOOLS);

export const isAudioTool = (toolId) => AUDIO_TOOLS.hasOwnProperty(toolId);

export const getToolById = (id) => TOOLS[id] || null;

export const getToolColor = (toolId) => {
  const colors = {
    sedaily: 'bg-sedaily-500/10 text-sedaily-500 border-sedaily-500/20',
    blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    rose: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
    pink: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
    cyan: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    violet: 'bg-violet-500/10 text-violet-500 border-violet-500/20',
    orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-500 border-fuchsia-500/20',
    teal: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
    lime: 'bg-lime-500/10 text-lime-500 border-lime-500/20',
    indigo: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
    purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    sky: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
    red: 'bg-red-500/10 text-red-500 border-red-500/20',
  };
  const tool = TOOLS[toolId];
  return tool ? colors[tool.color] : colors.sedaily;
};
